import { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Box, Grid, useTheme, useMediaQuery } from "@mui/material";

import { useTypedSelector } from "../../hooks.tsx";
import { searchFieldActions } from "../../store/search/searchField.tsx";
import TallyButton from "../common/TallyButton.tsx";

interface ExampleItemsProps {
  children?: React.ReactNode;
  isCircleView?: boolean;
}

interface ViewProps {
  exampleItems: string[];
  awaitingAskResponse: boolean;
  handleOnClick: React.MouseEventHandler<HTMLButtonElement>;
  isCircleView?: boolean;
}

const ITEM_HEIGHT = 26;
const ITEM_WIDTH = 140;

const MobileView: React.FC<ViewProps> = ({
  exampleItems,
  awaitingAskResponse,
  handleOnClick,
  isCircleView,
}) => {
  const [centerIndex, setCenterIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const loopedItems = [
    ...exampleItems.slice(-5),
    ...exampleItems,
    ...exampleItems.slice(0, 5),
  ];

  useEffect(() => {
    if (!scrollRef.current) return;

    const container = scrollRef.current;
    const total = exampleItems.length;

    if (isCircleView) {
      // vertical: scrollTop to middle
      container.scrollTop = total * ITEM_HEIGHT;
    } else {
      // horizontal: scrollLeft to middle
      container.scrollLeft = total * ITEM_WIDTH;
    }
  }, [exampleItems.length, isCircleView]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const idx = Math.round(container.scrollTop / ITEM_HEIGHT);
    setCenterIndex(idx);

    const total = exampleItems.length;
    const maxScroll = container.scrollHeight - container.clientHeight;

    // if we scrolled into the clones → jump back to the real list
    if (container.scrollTop <= ITEM_HEIGHT) {
      // at the top clones → jump to equivalent position at bottom
      container.scrollTop += total * ITEM_HEIGHT;
    } else if (container.scrollTop >= maxScroll - ITEM_HEIGHT) {
      // at the bottom clones → jump to equivalent position at top
      container.scrollTop -= total * ITEM_HEIGHT;
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%", // fills the bottom 1/3
        display: "flex",
        justifyContent: "center", // center scroll list vertically
        alignItems: "flex-start",
      }}
    >
      {/* Scrollable list */}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          overflowY: isCircleView ? "auto" : "hidden",
          overflowX: isCircleView ? "hidden" : "auto",
          height: isCircleView ? `${ITEM_HEIGHT * 3}px` : `${ITEM_HEIGHT}px`,
          width: isCircleView ? "100%" : "auto",
          scrollbarWidth: "none",
          display: !isCircleView ? "flex" : "block",
          flexDirection: !isCircleView ? "row" : undefined,
          alignItems: !isCircleView ? "center" : undefined,
          gap: !isCircleView ? "12px" : undefined,
          scrollSnapType: !isCircleView ? "x mandatory" : undefined,
          "&::-webkit-scrollbar": { display: "none" },
          // mask for horizontal only
          WebkitMaskImage: !isCircleView
            ? "linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)"
            : undefined,
          WebkitMaskRepeat: "no-repeat",
          WebkitMaskSize: "100% 100%",
        }}
      >
        {loopedItems.map((item, idx) => (
          <Box
            key={idx}
            sx={{
              scrollSnapAlign: "center",
              height: isCircleView ? `${ITEM_HEIGHT}px` : "100%",
              width: isCircleView ? "100%" : "auto",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexShrink: 0,
              opacity: !isCircleView ? 1 : idx === centerIndex ? 1 : 0.4,
              transform: !isCircleView
                ? "scale(1)"
                : idx === centerIndex
                  ? "scale(1)"
                  : "scale(0.9)",
              transition: "opacity 0.3s, transform 0.3s",
            }}
          >
            <TallyButton
              onClick={handleOnClick}
              disabled={awaitingAskResponse}
              sx={{
                borderColor: !isCircleView
                  ? "currentColor"
                  : idx === centerIndex
                    ? "currentColor"
                    : "rgba(0,0,0,0.2)",
                transition: "border-color 0.3s, border-width 0.3s",
              }}
            >
              {item}
            </TallyButton>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const DesktopView: React.FC<ViewProps> = ({
  exampleItems,
  awaitingAskResponse,
  handleOnClick,
}) => {
  return (
    <Grid
      container
      spacing={1.8}
      justifyContent="center"
      alignItems="center"
      direction="row"
    >
      {exampleItems.map((example: string) => (
        <Grid
          key={example}
          sx={{
            m: { sm: "0% 1.5% 0% 0%" },
            maxWidth: "100%",
          }}
        >
          <TallyButton onClick={handleOnClick} disabled={awaitingAskResponse}>
            {example}
          </TallyButton>
        </Grid>
      ))}
    </Grid>
  );
};

const ExampleItems: React.FC<ExampleItemsProps> = ({ isCircleView }) => {
  const dispatch = useDispatch();
  const exampleItems = useTypedSelector(
    (state) => state.exampleState.exampleItems,
  );
  const awaitingAskResponse = useTypedSelector(
    (state) => state.searchFieldState.awaitingAskResponse,
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Handle click on example button
  const handleOnClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    let searchText = event.currentTarget.textContent;
    dispatch(searchFieldActions.setTextAndSubmit(searchText));
  };

  if (isMobile) {
    return (
      <MobileView
        exampleItems={exampleItems}
        awaitingAskResponse={awaitingAskResponse}
        handleOnClick={handleOnClick}
        isCircleView={isCircleView}
      />
    );
  } else {
    return (
      <DesktopView
        exampleItems={exampleItems}
        awaitingAskResponse={awaitingAskResponse}
        handleOnClick={handleOnClick}
      />
    );
  }
};

export default ExampleItems;
